import { forwardRef, HTMLProps, ReactNode } from "react";

import CheckIcon from "@/assets/icons/check.svg";
import MinusIcon from "@/assets/icons/minus.svg";
import Typography from "@/components/Typography";
import { cx } from "@/utils";

import styles from "./Checkbox.module.css";

type CheckboxIndicatorProps = {
  indecisive?: boolean /** Adds a third state that can be used to display checked=true, as partial */;
};

export type CheckboxProps = {
  className?: string;
  block?: boolean;
  disabled?: boolean;
  label?: ReactNode;
  reverse?: boolean;
  errorMessage?: string;
  wrapLabel?: boolean;
  "data-testid"?: string;
} & React.InputHTMLAttributes<HTMLInputElement> &
  CheckboxIndicatorProps;

export const CheckboxIndicator = ({
  className,
  indecisive = false,
  ...props
}: HTMLProps<HTMLSpanElement> & CheckboxIndicatorProps) => (
  <span className={cx(styles.faux, className)} {...props}>
    {indecisive ? (
      <MinusIcon className={styles.check} />
    ) : (
      <CheckIcon className={styles.check} strokeWidth={2} />
    )}
  </span>
);

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      disabled,
      label = null,
      wrapLabel = false,
      className,
      errorMessage,
      block,
      reverse,
      "data-testid": testId,
      style,
      indecisive,
      ...props
    },
    ref
  ) => {
    return (
      <label
        style={style}
        className={cx(
          styles.root,
          {
            [styles.block]: block,
            [styles.reverse]: reverse,
            [styles.noWrapLabel]: wrapLabel === false,
            [styles.disabled]: disabled,
          },
          className
        )}
        data-testid={testId}
      >
        <input
          aria-disabled={disabled}
          className={styles.input}
          ref={ref}
          disabled={disabled}
          type="checkbox"
          {...props}
        />
        <CheckboxIndicator indecisive={indecisive} />
        {label && (
          <Typography
            className={styles.label}
            component="span"
            margin="none"
            variant="body-2"
          >
            {label} {errorMessage}
          </Typography>
        )}
      </label>
    );
  }
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
